﻿import { CommentsField } from "@/shared/components/CommentsField";
import {
	HoursMinutesTextField,
	toHumanDuration,
} from "@/shared/components/HoursMinutesTextField";
import { NumericTextField } from "@/shared/components/NumericTextField";
import { PercentField } from "@/shared/components/PercentField";
import { useCalendarPermissions } from "@/shared/hooks/useCalendarPermissions";
import { SectionSelector } from "@/shared/selectors";
import { BilletStatusSelector } from "@/shared/selectors/BilletStatusSelector";
import { EventTypeSelector } from "@/shared/selectors/EventTypeSelector";
import { InquiryStatusSelector } from "@/shared/selectors/InquiryStatusSelector";
import {
	ButtonWithLoading,
	ErrorModal,
	getMomentTz,
	HelmetDexteel,
	MesfModal,
	TimeService,
} from "@dexteel/mesf-core";
import { Calculate, ExpandMore } from "@mui/icons-material";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	Card,
	CardContent,
	Checkbox,
	FormControlLabel,
	Grid2,
	IconButton,
	InputAdornment,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { DatePicker, DateTimePicker } from "@mui/x-date-pickers";
import { useMutation } from "@tanstack/react-query";
import { round } from "lodash-es";
import type { Duration } from "moment";
import "moment-duration-format";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useCalendarAreaId } from "../../context/CalendarAreaContext";
import { calculateDeadline } from "../../hooks/useEventHandlers";
import {
	useSearchCampaignsByEventId,
	useSearchEvents,
} from "../../hooks/useSearchEvents";
import { Event, EVENT_INITIAL_VALUES } from "../../models/Event";
import {
	upsertEvent,
	upsertProductionEvent,
	upsertProductionEventActuals,
} from "../../repositories/CalendarRepository";
import {
	calculateFinishedYield,
	calculateMillYield,
	calculateNewEnd,
	calculateRollingHours,
	prorateScheduledByMonth,
} from "../../utils/calculateFields";
import { normalizeCalendarEventDateBoundary } from "../../utils/eventDateNormalization";
import {
	resolveTargetTPH,
	resolveTargetTPHPercentage,
	resolveTargetTPHValue,
} from "../../utils/targetTPH";
import { CampaignDataTable } from "../campaignTable/CampaignDataTable";
import { MILL_2 } from "@/setup/mill-config/mill-config";

const moment = getMomentTz();

type MonthScheduled = {
	ScheduledChargedTons: number | null;
	ScheduledRolledTons: number | null;
	ScheduledFinishedTons: number | null;
	ScheduledRollingHours: number | null;
};

const EMPTY_MONTH_SCHEDULED: MonthScheduled = {
	ScheduledChargedTons: null,
	ScheduledRolledTons: null,
	ScheduledFinishedTons: null,
	ScheduledRollingHours: null,
};

type MonthActuals = {
	ActualChargedTons: number | null;
	ActualRolledTons: number | null;
	ActualFinishedTons: number | null;
	ActualTPH: number | null;
	ActualRollingHours: number | null;
	ActualRollChangeHours: number | null;
	ActualMillYield: number | null;
	ActualFinishedYield: number | null;
};

const EMPTY_MONTH_ACTUALS: MonthActuals = {
	ActualChargedTons: null,
	ActualRolledTons: null,
	ActualFinishedTons: null,
	ActualTPH: null,
	ActualRollingHours: null,
	ActualRollChangeHours: null,
	ActualMillYield: null,
	ActualFinishedYield: null,
};

const getEventMonths = (start: Date, end: Date | null): Date[] => {
	const m1 = moment(start).startOf("month");
	if (!end) return [m1.toDate()];
	const m2 = moment(end).startOf("month");
	if (m2.isSame(m1, "month")) return [m1.toDate()];
	return [m1.toDate(), m2.toDate()];
};

const formatDuration = (start: Date, end: Date | null) => {
	if (!end) return "N/A";

	const diffMs = moment(end).diff(moment(start));

	return (moment.duration(diffMs) as Duration).format("d[d] h[h] m[m] s[s]");
};

export const EventModal = ({
	open,
	onHide,
	selectedEventId,
	isDraftMode,
	disabled = false,
	monthStart,
}: {
	open: boolean;
	onHide: (success: boolean) => void;
	selectedEventId: number | null;
	isDraftMode: boolean;
	disabled?: boolean;
	monthStart?: Date | null;
}) => {
	const defaultAreaId = useCalendarAreaId();
	const { canEditSchedule } = useCalendarPermissions();

	const [error, setError] = useState("");
	const [secondMonthActuals, setSecondMonthActuals] =
		useState<MonthActuals>(EMPTY_MONTH_ACTUALS);
	const [tooltipOpen, setTooltipOpen] = useState(false);
	const [datePickerOpen, setDatePickerOpen] = useState(false);
	const [expandedAccordions, setExpandedAccordions] = useState<
		("scheduled" | "actual")[]
	>(["scheduled", "actual"]);
	const [fieldsPrefilled, setFieldsPrefilled] = useState<
		Partial<
			Record<keyof Event, "prefilled" | "autocalculated" | "warning" | boolean>
		>
	>({
		End: false,
		ScheduledChargedTons: false,
		ScheduledRolledTons: false,
		ScheduledFinishedTons: false,
		BudgetTPH: false,
		ScheduledRollingHours: false,
		ScheduledRollChangeHours: false,
		ScheduledMillYield: false,
		ScheduledFinishedYield: false,
	});

	const { data: events } = useSearchEvents({
		eventId: selectedEventId ?? undefined,
		monthStart,
		stopRefetch: true,
		isDraftMode,
	});
	const event = events?.find(
		(e) => e.EventId === selectedEventId,
	) as Event | null;

	const { data: campaignData } = useSearchCampaignsByEventId({
		eventId: selectedEventId ?? 0,
		isDraftMode,
	});

	const { control, reset, handleSubmit, watch, getValues, setValue, trigger } =
		useForm<Event>({
			defaultValues: EVENT_INITIAL_VALUES,
			mode: "all",
		});

	const startDate = watch("Start");
	const endDate = watch("End");
	const deadline = watch("Deadline");
	const inquiryCloseDate = watch("InquiryCloseDate");
	const scheduledChargedTons = watch("ScheduledChargedTons");
	const scheduledRolledTons = watch("ScheduledRolledTons");
	const scheduledFinishedTons = watch("ScheduledFinishedTons");
	const scheduledRollingHours = watch("ScheduledRollingHours");
	const targetTPH = watch("TargetTPH");
	const targetTPHPercentage = watch("TargetTPHPercentage");
	const resolvedTargetTPH = resolveTargetTPH({
		TargetTPH: targetTPH ?? event?.TargetTPH,
		SectionTargetTPH: event?.SectionTargetTPH,
		TargetTPHPercentage: targetTPHPercentage,
		SectionTargetTPHPercentage: event?.SectionTargetTPHPercentage,
	});
	const triggerTargetTPHPercentageValidation = () => {
		queueMicrotask(() => {
			void trigger("TargetTPHPercentage");
		});
	};

	const deadlineStatus = () => {
		if (!deadline || !endDate || !startDate) return null;

		const deadlineTime = new Date(deadline).getTime();
		const startTime = new Date(startDate).getTime();
		const endTime = new Date(endDate).getTime();

		if (endTime < deadlineTime)
			return { type: "valid", text: "✓ Before deadline" } as const;
		if (deadlineTime > startTime && deadlineTime < endTime)
			return { type: "warning", text: "! Finishes after deadline" } as const;
		return { type: "error", text: "✕ Starts after deadline" } as const;
	};

	const inquiryCloseDateStatus = () => {
		if (!inquiryCloseDate || !endDate || !startDate) return null;

		const inquiryCloseDateTime = new Date(inquiryCloseDate).getTime();
		const startTime = new Date(startDate).getTime();
		const endTime = new Date(endDate).getTime();

		if (inquiryCloseDateTime >= startTime && inquiryCloseDateTime <= endTime)
			return null;

		if (inquiryCloseDateTime < startTime) return null;
		return "✕ Can't be after event end";
	};

	const resetFieldsPrefilled = () => {
		setFieldsPrefilled({
			ScheduledChargedTons: false,
			ScheduledRolledTons: false,
			ScheduledFinishedTons: false,
			BudgetTPH: false,
			ScheduledRollingHours: false,
			ScheduledRollChangeHours: false,
			ScheduledMillYield: false,
			ScheduledFinishedYield: false,
		});
	};

	const checkValueAndPrefill = async (
		actualField: keyof Event,
		newValue: number | null,
		filledType: "prefilled" | "autocalculated" | boolean = "prefilled",
		respectActualValue: boolean = true,
	) => {
		const actualValue = watch(actualField);

		if (!actualValue || !respectActualValue) {
			setValue(actualField, newValue ?? null);

			if (actualField === "TargetTPHPercentage") {
				triggerTargetTPHPercentageValidation();
			}

			setFieldsPrefilled((prev) => ({
				...prev,
				[actualField]: filledType,
			}));
		}

		if (!respectActualValue) {
			await trigger();
		}
	};

	const upsertEventMutation = useMutation(upsertEvent, {
		onSuccess: () => {
			onHide(true);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
	});
	const upsertProductionActualsMutation = useMutation(
		upsertProductionEventActuals,
		{
			onSuccess: () => {
				onHide(true);
			},
			onError: (error: Error) => {
				setError(error.message);
			},
		},
	);
	const upsertProductionEventMutation = useMutation(upsertProductionEvent, {
		onSuccess: async () => {
			if (secondActualRow && !isDraftMode) {
				await upsertProductionActualsMutation.mutate({
					eventId: selectedEventId!,
					monthStart: secondActualRow.MonthStart,
					actualChargedTons: secondMonthActuals.ActualChargedTons,
					actualRolledTons: secondMonthActuals.ActualRolledTons,
					actualFinishedTons: secondMonthActuals.ActualFinishedTons,
					actualTPH: secondMonthActuals.ActualTPH,
					actualRollingHours: secondMonthActuals.ActualRollingHours,
					actualRollChangeHours: secondMonthActuals.ActualRollChangeHours,
					actualMillYield: secondMonthActuals.ActualMillYield,
					actualFinishedYield: secondMonthActuals.ActualFinishedYield,
					defaultAreaId,
				});
			} else {
				onHide(true);
			}
		},
		onError: (error: Error) => {
			setError(error.message);
		},
	});

	const onSubmit: SubmitHandler<Event> = async (data) => {
		if (data.EventTypeCode === "P") {
			const effectiveTargetTPH = resolveTargetTPHValue({
				TargetTPH: data.TargetTPH,
				SectionTargetTPH: event?.SectionTargetTPH,
			});
			const effectiveTargetTPHPercentage = resolveTargetTPHPercentage({
				TargetTPHPercentage: data.TargetTPHPercentage,
				SectionTargetTPHPercentage: event?.SectionTargetTPHPercentage,
			});

			await upsertProductionEventMutation.mutateAsync({
				eventId: data?.EventId,
				start: data.Start!,
				end: data.End ?? null,
				sectionId: data.SectionId,
				statusCode: data.StatusCode,
				inquiryStatusCode: data.InquiryStatusCode,
				inquiryCloseDate: data.InquiryCloseDate,
				billetsStatusCode: data.BilletsStatusCode,
				deadline: data.Deadline,
				targetTPH: effectiveTargetTPH,
				targetTPHPercentage: effectiveTargetTPHPercentage,
				scheduledChargedTons: data.ScheduledChargedTons,
				scheduledRolledTons: data.ScheduledRolledTons,
				scheduledFinishedTons: data.ScheduledFinishedTons,
				budgetTPH: data.BudgetTPH,
				scheduledRollingHours: data.ScheduledRollingHours,
				scheduledRollChangeHours: data.ScheduledRollChangeHours,
				scheduledMillYield: data.ScheduledMillYield,
				scheduledFinishedYield: data.ScheduledFinishedYield,
				actualChargedTons: data.ActualChargedTons,
				actualRolledTons: data.ActualRolledTons,
				actualFinishedTons: data.ActualFinishedTons,
				actualTPH: data.ActualTPH,
				actualRollingHours: data.ActualRollingHours,
				actualRollChangeHours: data.ActualRollChangeHours,
				actualMillYield: data.ActualMillYield,
				actualFinishedYield: data.ActualFinishedYield,
				comments: data.EventComments,
				isDraftMode,
				defaultAreaId,
			});
		} else {
			const normalizedDates = normalizeCalendarEventDateBoundary({
				start: data.Start!,
				end: data.End ?? null,
			});

			upsertEventMutation.mutate({
				eventId: data.EventId,
				start: normalizedDates.start,
				end: normalizedDates.end,
				eventTypeCode: data.EventTypeCode!,
				comments: data.EventComments,
				isDraftMode,
				defaultAreaId,
			});
		}
	};

	const liveEnd = endDate ?? event?.End ?? null;
	const eventMonths = event ? getEventMonths(event.Start, liveEnd) : [];
	const isMultiMonth = eventMonths.length > 1;

	// Actual accordion months are billet-driven: they come from the ProductionEventActuals rows
	// that actually exist for the event (each with its shift-aligned MonthStart) -- NOT from the
	// scheduled date-based months. A month's actuals are only shown/editable once its row exists
	// (i.e. once a billet for that month has been detected).
	const actualRows = campaignData?.actuals ?? [];
	const firstActualRow = actualRows[0] ?? null;
	const secondActualRow = actualRows[1] ?? null;

	const [firstMonthScheduled, secondMonthScheduled] =
		isMultiMonth && event && liveEnd
			? prorateScheduledByMonth(event.Start, liveEnd, eventMonths[1], {
					ScheduledChargedTons: scheduledChargedTons,
					ScheduledRolledTons: scheduledRolledTons,
					ScheduledFinishedTons: scheduledFinishedTons,
					ScheduledRollingHours: scheduledRollingHours,
				})
			: [EMPTY_MONTH_SCHEDULED, EMPTY_MONTH_SCHEDULED];

	const isActualReadOnly = disabled || event?.StatusCode === "P" || isDraftMode;

	useEffect(() => {
		if (!open || !event) return;

		const actuals = campaignData?.actuals ?? [];
		const firstMonthActual = actuals[0];
		const secondMonthActual = actuals[1];

		const firstMonthActualHasData =
			!!firstMonthActual &&
			(firstMonthActual.ActualChargedTons !== null ||
				firstMonthActual.ActualRolledTons !== null ||
				firstMonthActual.ActualFinishedTons !== null);

		reset({
			...event,
			TargetTPH: resolveTargetTPHValue(event),
			TargetTPHPercentage: resolveTargetTPHPercentage(event),
			// Override actuals coming from event row (filtered by @MonthStart in the SP,
			// which is brittle across TZs / navigated months) with the first-month row
			// from GetCampaignsByEventId, which returns all per-month actuals unfiltered.
			...(firstMonthActualHasData
				? {
						ActualChargedTons: firstMonthActual.ActualChargedTons,
						ActualRolledTons: firstMonthActual.ActualRolledTons,
						ActualFinishedTons: firstMonthActual.ActualFinishedTons,
						ActualTPH: firstMonthActual.ActualTPH,
						ActualRollingHours: firstMonthActual.ActualRollingHours,
						ActualRollChangeHours: firstMonthActual.ActualRollChangeHours,
						ActualMillYield: firstMonthActual.ActualMillYield ?? 0,
						ActualFinishedYield: firstMonthActual.ActualFinishedYield ?? 0,
					}
				: {}),
		});
		resetFieldsPrefilled();

		setSecondMonthActuals(
			secondMonthActual
				? {
						ActualChargedTons: secondMonthActual.ActualChargedTons,
						ActualRolledTons: secondMonthActual.ActualRolledTons,
						ActualFinishedTons: secondMonthActual.ActualFinishedTons,
						ActualTPH: secondMonthActual.ActualTPH,
						ActualRollingHours: secondMonthActual.ActualRollingHours,
						ActualRollChangeHours: secondMonthActual.ActualRollChangeHours,
						ActualMillYield: secondMonthActual.ActualMillYield,
						ActualFinishedYield: secondMonthActual.ActualFinishedYield,
					}
				: EMPTY_MONTH_ACTUALS,
		);
	}, [event, open, campaignData]);

	const autocalculateUpdated = () => {
		const sameMillYield =
			(event?.SectionMillYield ?? null) === getValues("ScheduledMillYield");
		const sameFinishedYield =
			(event?.SectionFinishedYield ?? null) ===
			getValues("ScheduledFinishedYield");
		const sameChargedTons =
			round(getValues("ScheduledChargedTons") ?? 0) ===
			round(
				(getValues("ScheduledFinishedTons") ?? 0) /
					((event?.SectionFinishedYield ?? 1) / 100),
			);
		const sameRolledTons =
			round(getValues("ScheduledRolledTons") ?? 0) ===
			round(
				(getValues("ScheduledChargedTons") ?? 0) *
					((event?.SectionMillYield ?? 1) / 100),
			);
		const sameRollingHs =
			round(getValues("ScheduledRollingHours") ?? 0) ===
			round(
				(getValues("ScheduledRolledTons") ?? 0) / (getValues("BudgetTPH") ?? 1),
			);

		return (
			sameMillYield &&
			sameFinishedYield &&
			sameChargedTons &&
			sameRolledTons &&
			sameRollingHs
		);
	};

	const isAutoCalculated = autocalculateUpdated();

	if (!event) return null;

	return (
		<>
			{open ? (
				<HelmetDexteel
					title={`${event.EventTypeName} Event - ${event.SectionName}`}
				/>
			) : null}
			<MesfModal
				title={`EVENT #${event.EventId} - ${event.EventTypeName}`}
				open={open}
				id="event-detail-modal"
				maxWidth="xl"
				handleClose={() => onHide(false)}
				scroll="body"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content
						dividers
						sx={{
							backgroundColor: "#f6f6f6ff",
						}}
					>
						{event.EventTypeCode === "P" ? (
							<Grid2
								container
								spacing={1}
								sx={{
									alignItems: "flex-start",
								}}
							>
								<Grid2>
									<Card
										sx={{
											width: "100%",
										}}
									>
										<CardContent>
											<Grid2
												container
												size={12}
												spacing={2}
												alignItems="flex-start"
											>
												<Grid2
													size={{
														xs: 12,
														md: 2.4,
													}}
												>
													<Controller
														name="Start"
														control={control}
														rules={{
															required: "Start is required",
															deps: ["End"],
															validate: {
																notAfterEnd: (value) => {
																	if (
																		value &&
																		getValues("End") &&
																		value > getValues("End")!
																	) {
																		return "Start cannot be after End date.";
																	}
																	return true;
																},
															},
														}}
														render={({ field, fieldState: { error } }) => (
															<DateTimePicker
																label="Start"
																timezone={TimeService.getInstance().getServerTimeZone()}
																format="MM/DD/YY HH:mm"
																ampm={false}
																value={field.value ? moment(field.value) : null}
																onChange={field.onChange}
																maxDate={moment(endDate)}
																disabled
																slotProps={{
																	textField: {
																		size: "small",
																		fullWidth: true,
																		margin: "none",
																		variant: "outlined",
																		error: !!error,
																		helperText: error?.message,
																	},
																}}
															/>
														)}
													/>
												</Grid2>
												<Grid2
													size={{
														xs: 12,
														md: 2.4,
													}}
												>
													<Controller
														name="End"
														control={control}
														rules={{
															deps: ["Start"],
															validate: {
																notBeforeStart: (value) => {
																	if (
																		value &&
																		getValues("Start") &&
																		value < getValues("Start")
																	) {
																		return "End cannot be before Start.";
																	}
																	return true;
																},
																notSameAsStart: (value) => {
																	if (
																		value &&
																		startDate &&
																		moment(value).isSame(moment(startDate))
																	) {
																		return "End cannot be the same as Start.";
																	}
																	return true;
																},
															},
														}}
														render={({ field, fieldState: { error } }) => (
															<DateTimePicker
																label="End"
																timezone={TimeService.getInstance().getServerTimeZone()}
																format="MM/DD/YY HH:mm"
																ampm={false}
																value={field.value ? moment(field.value) : null}
																onChange={field.onChange}
																minDate={moment(startDate)}
																disabled={getValues("EventTypeCode") === "P"}
																slotProps={{
																	textField: {
																		size: "small",
																		fullWidth: true,
																		variant: "outlined",
																		margin: "none",
																		error: !!error,
																		helperText: error?.message
																			? error.message
																			: "",
																	},
																}}
															/>
														)}
													/>
												</Grid2>
												<Grid2
													size={{
														xs: 6,
														md: 2.4,
													}}
												>
													<Controller
														name="EventTypeCode"
														control={control}
														render={({ field, fieldState: { error } }) => (
															<EventTypeSelector
																value={field.value}
																onChange={(value: string) =>
																	field.onChange(value)
																}
																fieldError={error}
																disabled
															/>
														)}
													/>
												</Grid2>
												<Grid2 size={{ xs: 6, md: 2.4 }}>
													<TextField
														label="Duration"
														value={formatDuration(
															getValues("Start"),
															getValues("End"),
														)}
														fullWidth
														size="small"
														variant="outlined"
														disabled
														margin="none"
													/>
												</Grid2>
												{getValues("EventTypeCode") === "P" && (
													<Grid2 size={{ xs: 6, md: 2.4 }}>
														<Controller
															name="Deadline"
															control={control}
															render={({ field }) => (
																<Tooltip title={calculateDeadline(field.value)}>
																	<Box component="span">
																		<DateTimePicker
																			label="Rolling Deadline"
																			timezone={TimeService.getInstance().getServerTimeZone()}
																			format="MM/DD/YY HH:mm"
																			ampm={false}
																			value={
																				field.value ? moment(field.value) : null
																			}
																			onChange={field.onChange}
																			slotProps={{
																				textField: {
																					size: "small",
																					fullWidth: true,
																					margin: "none",
																					variant: "outlined",
																					helperText:
																						deadlineStatus()?.text ?? null,
																					FormHelperTextProps: {
																						sx: {
																							color:
																								deadlineStatus()?.type ===
																								"valid"
																									? (theme) =>
																											`${theme.palette.success.main} !important`
																									: deadlineStatus()?.type ===
																											"warning"
																										? (theme) =>
																												`${theme.palette.warning.main} !important`
																										: (theme) =>
																												`${theme.palette.error.main} !important`,
																						},
																					},
																				},
																			}}
																		/>
																	</Box>
																</Tooltip>
															)}
														/>
													</Grid2>
												)}

												{getValues("EventTypeCode") === "P" ? (
													<>
														<Grid2 size={{ xs: 6, md: 2.4 }}>
															<Controller
																name="SectionId"
																control={control}
																render={({ field, fieldState: { error } }) => (
																	<SectionSelector
																		value={field.value}
																		onChange={(newId: number | null) => {
																			field.onChange(newId);
																		}}
																		fieldError={error}
																		disabled
																	/>
																)}
															/>
														</Grid2>
														<Grid2
															size={{
																xs: 2.4,
															}}
														>
															<Controller
																name="BilletsStatusCode"
																control={control}
																render={({ field, fieldState: { error } }) => (
																	<BilletStatusSelector
																		value={field.value ?? null}
																		onChange={(value: string) =>
																			field.onChange(value)
																		}
																		fieldError={error}
																		disabled={disabled}
																	/>
																)}
															/>
														</Grid2>
														<Grid2
															size={{
																xs: 6,
																md: 2.4,
															}}
														>
															<Controller
																name="InquiryStatusCode"
																control={control}
																render={({ field, fieldState: { error } }) => (
																	<Tooltip
																		title={
																			inquiryCloseDate &&
																			new Date(inquiryCloseDate)?.getTime() <
																				new Date().getTime()
																				? "The inquiry close date has expired so the status can't be changed"
																				: undefined
																		}
																	>
																		<Box>
																			<InquiryStatusSelector
																				value={field.value}
																				onChange={(value: string) =>
																					field.onChange(value)
																				}
																				fieldError={error}
																				disabled={
																					disabled ||
																					(inquiryCloseDate
																						? new Date(
																								inquiryCloseDate,
																							)?.getTime() <
																							new Date().getTime()
																						: false)
																				}
																			/>
																		</Box>
																	</Tooltip>
																)}
															/>
														</Grid2>
														<Grid2 size={{ xs: 6, md: 2.4 }}>
															<Controller
																name="InquiryCloseDate"
																control={control}
																render={({ field, fieldState: { error } }) => (
																	<Tooltip
																		title="If this date is reached, the system must automatically switch the Inquiry Status to 'Closed'"
																		open={tooltipOpen && !datePickerOpen}
																		onOpen={() => setTooltipOpen(true)}
																		onClose={() => setTooltipOpen(false)}
																		enterDelay={300}
																		placement="right"
																	>
																		<Box component="span">
																			<DatePicker
																				label="Inquiry Close Date"
																				timezone={TimeService.getInstance().getServerTimeZone()}
																				format="MM/DD/YY"
																				value={
																					field.value
																						? moment(field.value)
																						: null
																				}
																				onChange={(value) => {
																					field.onChange(value);
																					if (
																						value &&
																						value.toDate().getTime() <
																							moment().toDate().getTime()
																					) {
																						setValue("InquiryStatusCode", "C");
																					}
																				}}
																				onOpen={() => {
																					setDatePickerOpen(true);
																					setTooltipOpen(false);
																				}}
																				onClose={() => {
																					setDatePickerOpen(false);
																				}}
																				slotProps={{
																					textField: {
																						size: "small",
																						fullWidth: true,
																						margin: "none",
																						variant: "outlined",
																						error: !!error,
																						helperText:
																							inquiryCloseDateStatus() ?? null,
																						FormHelperTextProps: {
																							sx: {
																								color: inquiryCloseDateStatus()
																									? (theme) =>
																											`${theme.palette.error.main} !important`
																									: undefined,
																							},
																						},
																					},
																				}}
																			/>
																		</Box>
																	</Tooltip>
																)}
															/>
														</Grid2>
													</>
												) : null}
											</Grid2>
										</CardContent>
									</Card>
								</Grid2>

								<Grid2 size={{ md: 12 }}>
									<Accordion
										expanded={expandedAccordions.includes("scheduled")}
										onChange={(e, expanded) => {
											const target = e.target as HTMLElement;
											if (
												target.closest(".MuiAccordionSummary-expandIconWrapper")
											) {
												if (expanded) {
													setExpandedAccordions([
														...expandedAccordions,
														"scheduled",
													]);
												} else {
													setExpandedAccordions(
														expandedAccordions.filter((e) => e !== "scheduled"),
													);
												}
											}
										}}
										data-test-id="accordion-scheduled"
									>
										<AccordionSummary
											expandIcon={
												<Box display="flex" alignItems="center">
													<ExpandMore sx={{ cursor: "pointer !important" }} />
													{expandedAccordions.includes("scheduled") ? (
														<Tooltip
															title={`Autocalculate fields based on Scheduled Finished Tons, Section TPH, Section Mill Yield & Section Finished Yield`}
														>
															<IconButton
																size="small"
																sx={{
																	cursor: isAutoCalculated
																		? "default !important"
																		: "auto",
																}}
																onClick={(e) => {
																	e.stopPropagation();

																	if (isAutoCalculated) return;

																	checkValueAndPrefill(
																		"ScheduledMillYield",
																		event?.SectionMillYield ?? null,
																		"prefilled",
																		false,
																	);
																	checkValueAndPrefill(
																		"ScheduledFinishedYield",
																		event?.SectionFinishedYield ?? null,
																		"prefilled",
																		false,
																	);
																	checkValueAndPrefill(
																		"BudgetTPH",
																		event?.TonsPerHour ?? null,
																		"prefilled",
																		false,
																	);
																	checkValueAndPrefill(
																		"TargetTPH",
																		event?.SectionTargetTPH ?? null,
																		"prefilled",
																		false,
																	);
																	checkValueAndPrefill(
																		"TargetTPHPercentage",
																		event?.SectionTargetTPHPercentage ?? 80,
																		"prefilled",
																		false,
																	);

																	const newChargeTons =
																		(getValues("ScheduledFinishedTons") ?? 0) /
																		((event?.SectionFinishedYield ?? 1) / 100);
																	const newRolledTons =
																		newChargeTons *
																		((event?.SectionMillYield ?? 1) / 100);
																	const newRollingHs = calculateRollingHours(
																		newRolledTons,
																		event?.TonsPerHour ?? 1,
																	);

																	checkValueAndPrefill(
																		"ScheduledRollingHours",
																		Number(newRollingHs),
																		"autocalculated",
																		false,
																	);

																	checkValueAndPrefill(
																		"ScheduledChargedTons",
																		newChargeTons,
																		"autocalculated",
																		false,
																	);
																	checkValueAndPrefill(
																		"ScheduledRolledTons",
																		newRolledTons,
																		"autocalculated",
																		false,
																	);

																	calculateNewEnd({
																		startDate,
																		estimatedRollingHs: Number(newRollingHs),
																		setValue,
																		setFieldsPrefilled,
																	});
																}}
															>
																<Calculate
																	fontSize="small"
																	color={
																		isAutoCalculated ? "disabled" : "primary"
																	}
																/>
															</IconButton>
														</Tooltip>
													) : null}
												</Box>
											}
											sx={{
												cursor: "default !important",
											}}
										>
											<Typography
												variant="h6"
												color="textSecondary"
												fontSize={14}
											>
												Scheduled
											</Typography>
										</AccordionSummary>
										<AccordionDetails>
											<Grid2 container spacing={2} width="100%">
												<Grid2 size={{ md: 9.6 }}>
													<Grid2 container spacing={2}>
														<Grid2 size={{ md: 3 }}>
															<Controller
																name="ScheduledChargedTons"
																control={control}
																rules={{
																	deps: ["ScheduledRolledTons"],
																	validate: {
																		minValue: (value) => {
																			const rolledTons = getValues(
																				"ScheduledRolledTons",
																			);
																			if ((value ?? 0) < (rolledTons ?? 0)) {
																				return "Must be greater or equal than to Rolled Tons";
																			}
																			return true;
																		},
																	},
																}}
																render={({ field, fieldState: { error } }) => (
																	<NumericTextField
																		label="Charged Tons"
																		value={field.value}
																		onChange={(value: number | null) => {
																			field.onChange(value);

																			const finishedTons = watch(
																				"ScheduledFinishedTons",
																			);
																			const rolledTons = watch(
																				"ScheduledRolledTons",
																			);

																			const newMillYield = calculateMillYield(
																				value ?? 0,
																				rolledTons ?? 1,
																			);
																			const newFinishedYield =
																				calculateFinishedYield(
																					finishedTons ?? 0,
																					value ?? 1,
																				);

																			checkValueAndPrefill(
																				"ScheduledMillYield",
																				newMillYield,
																				newMillYield !== event?.SectionMillYield
																					? "autocalculated"
																					: "prefilled",
																				false,
																			);
																			checkValueAndPrefill(
																				"ScheduledFinishedYield",
																				newFinishedYield,
																				newFinishedYield !==
																					event?.SectionFinishedYield
																					? "autocalculated"
																					: "prefilled",
																				false,
																			);

																			const expectedValue = round(
																				(getValues("ScheduledFinishedTons") ??
																					0) /
																					((getValues(
																						"ScheduledFinishedYield",
																					) ?? 1) /
																						100),
																			);

																			setFieldsPrefilled((prev) => ({
																				...prev,
																				ScheduledChargedTons: false,
																				ScheduledRolledTons: false,
																			}));
																		}}
																		showDecimals={true}
																		margin="none"
																		error={!!error}
																		helperText={
																			error?.message
																				? error.message
																				: fieldsPrefilled.ScheduledChargedTons
																					? "Autocalculated (Fin. Tons / Fin. Yield)"
																					: ""
																		}
																		disabled={
																			disabled ||
																			(event.StatusCode !== "P" &&
																				event.StatusCode !== "I")
																		}
																	/>
																)}
															/>
														</Grid2>
														<Grid2 size={{ md: 3 }}>
															<Controller
																name="ScheduledRolledTons"
																control={control}
																rules={{
																	deps: [
																		"ScheduledChargedTons",
																		"ScheduledFinishedTons",
																	],
																	validate: {
																		maxValue: (value) => {
																			const chargeTons = getValues(
																				"ScheduledChargedTons",
																			);
																			if ((value ?? 0) > (chargeTons ?? 0)) {
																				return "Must be less or equal than to Charge Tons";
																			}
																			return true;
																		},
																		minValue: (value) => {
																			const finishedTons = getValues(
																				"ScheduledFinishedTons",
																			);
																			if ((value ?? 0) < (finishedTons ?? 0)) {
																				return "Must be greater or equal than to Finished Tons";
																			}
																			return true;
																		},
																	},
																}}
																render={({ field, fieldState: { error } }) => (
																	<NumericTextField
																		label="Rolled Tons"
																		value={field.value}
																		onChange={(value: number | null) => {
																			const scheduledMillYield =
																				watch("ScheduledMillYield");
																			field.onChange(value);

																			const chargeTons = watch(
																				"ScheduledChargedTons",
																			);
																			const tonsPerHour = watch("BudgetTPH");

																			const newMillYield = calculateMillYield(
																				chargeTons ?? 0,
																				value ?? 1,
																			);
																			const newRollingHs =
																				calculateRollingHours(
																					value ?? 0,
																					tonsPerHour ?? 1,
																				);

																			checkValueAndPrefill(
																				"ScheduledMillYield",
																				newMillYield,
																				newMillYield !== event?.SectionMillYield
																					? "autocalculated"
																					: "prefilled",
																				false,
																			);
																			checkValueAndPrefill(
																				"ScheduledRollingHours",
																				Number(newRollingHs),
																				"autocalculated",
																				false,
																			);

																			calculateNewEnd({
																				startDate,
																				estimatedRollingHs:
																					Number(newRollingHs),
																				setValue,
																				setFieldsPrefilled,
																			});

																			const expectedValue = round(
																				(getValues("ScheduledChargedTons") ??
																					0) *
																					((getValues("ScheduledMillYield") ??
																						1) /
																						100),
																			);

																			setFieldsPrefilled((prev) => ({
																				...prev,
																				ScheduledRolledTons: false,
																			}));
																		}}
																		showDecimals={true}
																		margin="none"
																		error={!!error}
																		helperText={
																			error?.message
																				? error.message
																				: fieldsPrefilled.ScheduledRolledTons
																					? "Autocalculated (Charge Tons * Mill Yield)"
																					: ""
																		}
																		disabled={
																			disabled ||
																			(event.StatusCode !== "P" &&
																				event.StatusCode !== "I")
																		}
																	/>
																)}
															/>
														</Grid2>
														<Grid2 size={{ md: 3 }}>
															<Controller
																name="ScheduledFinishedTons"
																control={control}
																rules={{
																	deps: ["ScheduledRolledTons"],
																	validate: {
																		maxValue: (value) => {
																			const rolledTons = getValues(
																				"ScheduledRolledTons",
																			);
																			if ((value ?? 0) > (rolledTons ?? 0)) {
																				return "Must be less or equal than to Rolled Tons";
																			}
																			return true;
																		},
																	},
																}}
																render={({ field, fieldState: { error } }) => (
																	<NumericTextField
																		label="Finished Tons"
																		value={field.value}
																		onChange={(value: number | null) => {
																			const scheduledFinishedYield = watch(
																				"ScheduledFinishedYield",
																			);
																			field.onChange(value);

																			const chargeTons = watch(
																				"ScheduledChargedTons",
																			);
																			const newFinishedYield =
																				calculateFinishedYield(
																					value ?? 0,
																					chargeTons ?? 1,
																				);
																			checkValueAndPrefill(
																				"ScheduledFinishedYield",
																				newFinishedYield,
																				newFinishedYield !==
																					event?.SectionFinishedYield
																					? "autocalculated"
																					: "prefilled",
																				false,
																			);

																			setFieldsPrefilled((prev) => ({
																				...prev,
																				ScheduledFinishedTons: false,
																			}));
																		}}
																		showDecimals={true}
																		margin="none"
																		error={!!error}
																		helperText={
																			error?.message
																				? error.message
																				: fieldsPrefilled.ScheduledFinishedTons
																					? ""
																					: ""
																		}
																		disabled={
																			disabled ||
																			(event.StatusCode !== "P" &&
																				event.StatusCode !== "I")
																		}
																	/>
																)}
															/>
														</Grid2>
														<Grid2 size={{ md: 3 }}>
															<Controller
																name="ScheduledRollingHours"
																control={control}
																render={({ field }) => (
																	<NumericTextField
																		label="Rolling Hours"
																		value={field.value}
																		onChange={field.onChange}
																		showDecimals={false}
																		helperText={
																			fieldsPrefilled.ScheduledRollingHours
																				? "Autocalculated (Rolled Tons / TPH)"
																				: ""
																		}
																		InputProps={{
																			endAdornment: (
																				<InputAdornment position="end">
																					hs
																				</InputAdornment>
																			),
																		}}
																		disabled
																	/>
																)}
															/>
														</Grid2>
														{canEditSchedule ? (
															<Grid2 size={{ md: 1.5 }}>
																<Controller
																	name="BudgetTPH"
																	control={control}
																	render={({
																		field,
																		fieldState: { error },
																	}) => (
																		<NumericTextField
																			label="Budget TPH"
																			value={field.value}
																			onChange={(value) => {
																				field.onChange(value);

																				const rolledTons = watch(
																					"ScheduledRolledTons",
																				);

																				const newRollingHs =
																					calculateRollingHours(
																						rolledTons ?? 0,
																						value ?? 1,
																					);
																				checkValueAndPrefill(
																					"ScheduledRollingHours",
																					Number(newRollingHs),
																					"autocalculated",
																					false,
																				);

																				calculateNewEnd({
																					startDate,
																					estimatedRollingHs:
																						Number(newRollingHs),
																					setValue,
																					setFieldsPrefilled,
																				});

																				setFieldsPrefilled((prev) => ({
																					...prev,
																					BudgetTPH:
																						value ===
																						round(event?.TonsPerHour ?? 0)
																							? "prefilled"
																							: false,
																				}));
																			}}
																			onBlur={field.onBlur}
																			showDecimals
																			error={!!error}
																			helperText={
																				error?.message ? error.message : ""
																			}
																			disabled={
																				disabled ||
																				(event.StatusCode !== "P" &&
																					event.StatusCode !== "I")
																			}
																		/>
																	)}
																/>
															</Grid2>
														) : null}
														<Grid2 size={{ md: 1.5 }}>
															<Controller
																name="TargetTPH"
																control={control}
																render={({ field, fieldState: { error } }) => (
																	<NumericTextField
																		label="Target TPH"
																		value={field.value}
																		onChange={(value) => {
																			field.onChange(value);
																			setFieldsPrefilled((prev) => ({
																				...prev,
																				TargetTPH:
																					value ===
																					(event?.SectionTargetTPH ?? null)
																						? "prefilled"
																						: false,
																			}));
																		}}
																		onBlur={field.onBlur}
																		showDecimals
																		error={!!error}
																		helperText={
																			error?.message ? error.message : ""
																		}
																		disabled={
																			disabled ||
																			(event.StatusCode !== "P" &&
																				event.StatusCode !== "I")
																		}
																	/>
																)}
															/>
														</Grid2>
														<Grid2 size={{ md: 3 }}>
															<Grid2 container spacing={2}>
																<Grid2 size={6}>
																	<Controller
																		name="TargetTPHPercentage"
																		control={control}
																		rules={{
																			validate: (value) => {
																				if (value != null && value <= 0) {
																					return "Must be greater than 0%";
																				}

																				return true;
																			},
																		}}
																		render={({
																			field,
																			fieldState: { error },
																		}) => (
																			<PercentField
																				label="TPH Pct."
																				value={field.value}
																				onChange={(value) => {
																					field.onChange(value);
																					setFieldsPrefilled((prev) => ({
																						...prev,
																						TargetTPHPercentage:
																							value ===
																							(event?.SectionTargetTPHPercentage ??
																								80)
																								? "prefilled"
																								: false,
																					}));
																				}}
																				error={!!error}
																				helperText={error?.message ?? ""}
																				disabled={
																					disabled ||
																					(event.StatusCode !== "P" &&
																						event.StatusCode !== "I")
																				}
																			/>
																		)}
																	/>
																</Grid2>
																<Grid2 size={6}>
																	<NumericTextField
																		label="Effective TPH"
																		value={resolvedTargetTPH}
																		onChange={() => {}}
																		showDecimals
																		disabled
																	/>
																</Grid2>
															</Grid2>
														</Grid2>
														<Grid2 size={{ md: 3 }}>
															<Controller
																name="ScheduledMillYield"
																control={control}
																rules={{
																	deps: [
																		"ScheduledChargedTons",
																		"ScheduledRolledTons",
																	],
																	min: {
																		value: 0,
																		message: "Must be at least 0%",
																	},
																	max: {
																		value: 100,
																		message:
																			"Must be less than or equal to 100%",
																	},
																}}
																render={({ field, fieldState: { error } }) => (
																	<PercentField
																		label="Mill Yield"
																		value={field.value}
																		onChange={field.onChange}
																		error={!!error}
																		helperText={
																			error?.message
																				? error.message
																				: fieldsPrefilled.ScheduledMillYield ===
																						"autocalculated"
																					? "Autocalculated (Roll. Tons / Charge Tons)"
																					: ""
																		}
																		disabled
																	/>
																)}
															/>
														</Grid2>
														<Grid2 size={{ md: 3 }}>
															<Controller
																name="ScheduledFinishedYield"
																control={control}
																rules={{
																	deps: [
																		"ScheduledChargedTons",
																		"ScheduledFinishedTons",
																	],
																	min: {
																		value: 0,
																		message: "Must be at least 0%",
																	},
																	max: {
																		value: 100,
																		message:
																			"Must be less than or equal to 100%",
																	},
																}}
																render={({ field, fieldState: { error } }) => (
																	<PercentField
																		label="Finished Yield"
																		value={field.value}
																		onChange={field.onChange}
																		error={!!error}
																		helperText={
																			error?.message
																				? error.message
																				: fieldsPrefilled.ScheduledFinishedYield ===
																						"autocalculated"
																					? "Autocalculated (Fin. Tons / Charge Tons)"
																					: ""
																		}
																		disabled
																	/>
																)}
															/>
														</Grid2>
													</Grid2>
												</Grid2>
												<Grid2
													size={{ md: 2.4 }}
													sx={{ display: "flex", flexDirection: "column" }}
												>
													<Box
														sx={{
															border: 1,
															borderColor: "divider",
															borderRadius: 1,
															px: 2,
															py: 1,
															display: "flex",
															flexDirection: "column",
															justifyContent: "center",
															height: "100%",
														}}
													>
														<Controller
															name="ScheduledRollChangeHours"
															control={control}
															rules={{
																validate: (value) => {
																	if (value == null) return true;
																	if (!startDate || !endDate) return true;
																	const eventHours = moment(endDate).diff(
																		moment(startDate),
																		"hours",
																		true,
																	);
																	if (value > eventHours)
																		return "Exceeds event duration";
																	return true;
																},
															}}
															render={({
																field,
																fieldState: { error: fieldError },
															}) => (
																<HoursMinutesTextField
																	label="Roll Change Time"
																	value={field.value}
																	onChange={(value) => {
																		field.onChange(value === 0 ? null : value);
																		setFieldsPrefilled((prev) => ({
																			...prev,
																			ScheduledRollChangeHours: false,
																		}));
																		calculateNewEnd({
																			startDate,
																			estimatedRollingHs:
																				getValues("ScheduledRollingHours") ?? 0,
																			setValue,
																			setFieldsPrefilled,
																		});
																	}}
																	error={!!fieldError}
																	helperText={
																		fieldError?.message ??
																		(fieldsPrefilled.ScheduledRollChangeHours ===
																		"autocalculated"
																			? "Auto-filled from history"
																			: "")
																	}
																	disabled={
																		disabled ||
																		(event.StatusCode !== "P" &&
																			event.StatusCode !== "I")
																	}
																/>
															)}
														/>
														<Box
															sx={{
																display: "flex",
																justifyContent: "space-between",
																mt: 0.5,
															}}
														>
															<Box sx={{ textAlign: "center" }}>
																<Typography
																	variant="caption"
																	color="text.disabled"
																	display="block"
																	sx={{ fontSize: "0.6rem", lineHeight: 1.2 }}
																>
																	Min
																</Typography>
																<Typography
																	variant="body2"
																	color="text.disabled"
																	sx={{ fontSize: "0.7rem" }}
																>
																	{toHumanDuration(
																		campaignData?.rollChangeStats
																			?.HistoricalMin,
																	) || "—"}
																</Typography>
															</Box>
															<Box sx={{ textAlign: "center" }}>
																<Typography
																	variant="caption"
																	color="text.disabled"
																	display="block"
																	sx={{ fontSize: "0.6rem", lineHeight: 1.2 }}
																>
																	Max
																</Typography>
																<Typography
																	variant="body2"
																	color="text.disabled"
																	sx={{ fontSize: "0.7rem" }}
																>
																	{toHumanDuration(
																		campaignData?.rollChangeStats
																			?.HistoricalMax,
																	) || "—"}
																</Typography>
															</Box>
															<Box sx={{ textAlign: "center" }}>
																<Typography
																	variant="caption"
																	color="text.disabled"
																	display="block"
																	sx={{ fontSize: "0.6rem", lineHeight: 1.2 }}
																>
																	Avg top 5
																</Typography>
																<Typography
																	variant="body2"
																	color="text.disabled"
																	sx={{ fontSize: "0.7rem" }}
																>
																	{toHumanDuration(
																		campaignData?.rollChangeStats
																			?.HistoricalAvgBest5,
																	) || "—"}
																</Typography>
															</Box>
														</Box>
													</Box>
												</Grid2>
											</Grid2>

											{isMultiMonth && (
												<>
													<Typography
														variant="caption"
														color="text.secondary"
														sx={{
															mt: 2,
															mb: 1,
															display: "block",
															fontWeight: 600,
														}}
													>
														{moment(eventMonths[0]).format("MMMM YYYY")}
													</Typography>
													<Grid2 container spacing={2} width="100%">
														{(
															[
																"ScheduledChargedTons",
																"ScheduledRolledTons",
																"ScheduledFinishedTons",
																"ScheduledRollingHours",
															] as const
														).map((field) => (
															<Grid2 key={field} size={{ md: 3 }}>
																<NumericTextField
																	label={
																		field === "ScheduledRollingHours"
																			? "Rolling Hours"
																			: field
																					.replace("Scheduled", "")
																					.replace(/([A-Z])/g, " $1")
																					.trim()
																	}
																	value={firstMonthScheduled[field]}
																	onChange={() => {}}
																	showDecimals={
																		field !== "ScheduledRollingHours"
																	}
																	disabled
																	InputProps={
																		field === "ScheduledRollingHours"
																			? {
																					endAdornment: (
																						<InputAdornment position="end">
																							hs
																						</InputAdornment>
																					),
																				}
																			: undefined
																	}
																/>
															</Grid2>
														))}
													</Grid2>
													<Typography
														variant="caption"
														color="text.secondary"
														sx={{
															mt: 2,
															mb: 1,
															display: "block",
															fontWeight: 600,
														}}
													>
														{moment(eventMonths[1]).format("MMMM YYYY")}
													</Typography>
													<Grid2 container spacing={2} width="100%">
														{(
															[
																"ScheduledChargedTons",
																"ScheduledRolledTons",
																"ScheduledFinishedTons",
																"ScheduledRollingHours",
															] as const
														).map((field) => (
															<Grid2 key={field} size={{ md: 3 }}>
																<NumericTextField
																	label={
																		field === "ScheduledRollingHours"
																			? "Rolling Hours"
																			: field
																					.replace("Scheduled", "")
																					.replace(/([A-Z])/g, " $1")
																					.trim()
																	}
																	value={secondMonthScheduled[field]}
																	onChange={() => {}}
																	showDecimals={
																		field !== "ScheduledRollingHours"
																	}
																	disabled
																	InputProps={
																		field === "ScheduledRollingHours"
																			? {
																					endAdornment: (
																						<InputAdornment position="end">
																							hs
																						</InputAdornment>
																					),
																				}
																			: undefined
																	}
																/>
															</Grid2>
														))}
													</Grid2>
												</>
											)}
										</AccordionDetails>
									</Accordion>
								</Grid2>

								{(!isDraftMode ||
									event?.StatusCode === "I" ||
									event?.StatusCode === "C" ||
									event?.StatusCode === "K") && (
									<Grid2 size={{ md: 12 }}>
										<Accordion
											expanded={expandedAccordions.includes("actual")}
											onChange={(e, expanded) => {
												const target = e.target as HTMLElement;
												if (
													target.closest(
														".MuiAccordionSummary-expandIconWrapper",
													)
												) {
													if (expanded) {
														setExpandedAccordions([
															...expandedAccordions,
															"actual",
														]);
													} else {
														setExpandedAccordions(
															expandedAccordions.filter((e) => e !== "actual"),
														);
													}
												}
											}}
											data-test-id="accordion-actual"
										>
											<AccordionSummary
												expandIcon={
													<ExpandMore sx={{ cursor: "pointer !important" }} />
												}
												sx={{
													cursor: "default !important",
												}}
											>
												<Typography
													variant="h6"
													color="textSecondary"
													fontSize={14}
												>
													Actual
												</Typography>
											</AccordionSummary>
											<AccordionDetails>
												{secondActualRow && (
													<Typography
														variant="caption"
														color="text.secondary"
														sx={{ mb: 1, display: "block", fontWeight: 600 }}
													>
														{moment
															.utc(firstActualRow?.MonthStart)
															.format("MMMM YYYY")}
													</Typography>
												)}
												<Grid2 container spacing={2} width="100%">
													<Grid2 size={{ md: 1.5 }}>
														<Controller
															name="ActualChargedTons"
															control={control}
															render={({ field }) => (
																<NumericTextField
																	label="Charged Tons"
																	value={field.value}
																	onChange={field.onChange}
																	showDecimals
																	disabled={isActualReadOnly}
																/>
															)}
														/>
													</Grid2>
													<Grid2 size={{ md: 1.5 }}>
														<Controller
															name="ActualRolledTons"
															control={control}
															render={({ field }) => (
																<NumericTextField
																	label="Rolled Tons"
																	value={field.value}
																	onChange={field.onChange}
																	showDecimals
																	disabled={isActualReadOnly}
																/>
															)}
														/>
													</Grid2>
													<Grid2 size={{ md: 1.5 }}>
														<Controller
															name="ActualFinishedTons"
															control={control}
															render={({ field }) => (
																<NumericTextField
																	label="Finished Tons"
																	value={field.value}
																	onChange={field.onChange}
																	showDecimals
																	disabled={isActualReadOnly}
																/>
															)}
														/>
													</Grid2>
													<Grid2 size={{ md: 1.5 }}>
														<Controller
															name="ActualTPH"
															control={control}
															render={({ field }) => (
																<NumericTextField
																	label="TPH"
																	value={field.value}
																	onChange={field.onChange}
																	showDecimals
																	disabled={isActualReadOnly}
																/>
															)}
														/>
													</Grid2>
													<Grid2 size={{ md: 1.5 }}>
														<Controller
															name="ActualRollingHours"
															control={control}
															render={({ field }) => (
																<NumericTextField
																	label="Rolling Hours"
																	value={field.value}
																	onChange={field.onChange}
																	showDecimals={false}
																	InputProps={{
																		endAdornment: (
																			<InputAdornment position="end">
																				hs
																			</InputAdornment>
																		),
																	}}
																	disabled={isActualReadOnly}
																/>
															)}
														/>
													</Grid2>
													<Grid2 size={{ md: 1.5 }}>
														<Controller
															name="ActualRollChangeHours"
															control={control}
															render={({ field }) => (
																<HoursMinutesTextField
																	label="Roll Change Time"
																	value={field.value}
																	onChange={(value) => {
																		field.onChange(value === 0 ? null : value);
																	}}
																	disabled={isActualReadOnly}
																/>
															)}
														/>
													</Grid2>
													<Grid2 size={{ md: 1.5 }}>
														<Controller
															name="ActualMillYield"
															control={control}
															rules={{
																min: {
																	value: 0,
																	message: "Must be at least 0%",
																},
																max: {
																	value: 100,
																	message: "Must be less than or equal to 100%",
																},
															}}
															render={({ field, fieldState: { error } }) => (
																<PercentField
																	label="Mill Yield"
																	value={field.value}
																	onChange={field.onChange}
																	error={!!error}
																	helperText={error?.message}
																	disabled={isActualReadOnly}
																/>
															)}
														/>
													</Grid2>
													<Grid2 size={{ md: 1.5 }}>
														<Controller
															name="ActualFinishedYield"
															control={control}
															rules={{
																min: {
																	value: 0,
																	message: "Must be at least 0%",
																},
																max: {
																	value: 100,
																	message: "Must be less than or equal to 100%",
																},
															}}
															render={({ field, fieldState: { error } }) => (
																<PercentField
																	label="Finished Yield"
																	value={field.value}
																	onChange={field.onChange}
																	error={!!error}
																	helperText={error?.message}
																	disabled={isActualReadOnly}
																/>
															)}
														/>
													</Grid2>
												</Grid2>

												{secondActualRow && (
													<>
														<Typography
															variant="caption"
															color="text.secondary"
															sx={{
																mt: 2,
																mb: 1,
																display: "block",
																fontWeight: 600,
															}}
														>
															{moment
																.utc(secondActualRow.MonthStart)
																.format("MMMM YYYY")}
														</Typography>
														<Grid2 container spacing={2} width="100%">
															<Grid2 size={{ md: 1.5 }}>
																<NumericTextField
																	label="Charged Tons"
																	value={secondMonthActuals.ActualChargedTons}
																	onChange={(v) =>
																		setSecondMonthActuals((p) => ({
																			...p,
																			ActualChargedTons: v,
																		}))
																	}
																	showDecimals
																	disabled={isActualReadOnly}
																/>
															</Grid2>
															<Grid2 size={{ md: 1.5 }}>
																<NumericTextField
																	label="Rolled Tons"
																	value={secondMonthActuals.ActualRolledTons}
																	onChange={(v) =>
																		setSecondMonthActuals((p) => ({
																			...p,
																			ActualRolledTons: v,
																		}))
																	}
																	showDecimals
																	disabled={isActualReadOnly}
																/>
															</Grid2>
															<Grid2 size={{ md: 1.5 }}>
																<NumericTextField
																	label="Finished Tons"
																	value={secondMonthActuals.ActualFinishedTons}
																	onChange={(v) =>
																		setSecondMonthActuals((p) => ({
																			...p,
																			ActualFinishedTons: v,
																		}))
																	}
																	showDecimals
																	disabled={isActualReadOnly}
																/>
															</Grid2>
															<Grid2 size={{ md: 1.5 }}>
																<NumericTextField
																	label="TPH"
																	value={secondMonthActuals.ActualTPH}
																	onChange={(v) =>
																		setSecondMonthActuals((p) => ({
																			...p,
																			ActualTPH: v,
																		}))
																	}
																	showDecimals
																	disabled={isActualReadOnly}
																/>
															</Grid2>
															<Grid2 size={{ md: 1.5 }}>
																<NumericTextField
																	label="Rolling Hours"
																	value={secondMonthActuals.ActualRollingHours}
																	onChange={(v) =>
																		setSecondMonthActuals((p) => ({
																			...p,
																			ActualRollingHours: v,
																		}))
																	}
																	showDecimals={false}
																	InputProps={{
																		endAdornment: (
																			<InputAdornment position="end">
																				hs
																			</InputAdornment>
																		),
																	}}
																	disabled={isActualReadOnly}
																/>
															</Grid2>
															<Grid2 size={{ md: 1.5 }}>
																<HoursMinutesTextField
																	label="Roll Change Time"
																	value={
																		secondMonthActuals.ActualRollChangeHours
																	}
																	onChange={(value) =>
																		setSecondMonthActuals((p) => ({
																			...p,
																			ActualRollChangeHours:
																				value === 0 ? null : value,
																		}))
																	}
																	disabled={isActualReadOnly}
																/>
															</Grid2>
															<Grid2 size={{ md: 1.5 }}>
																<PercentField
																	label="Mill Yield"
																	value={secondMonthActuals.ActualMillYield}
																	onChange={(v) =>
																		setSecondMonthActuals((p) => ({
																			...p,
																			ActualMillYield: v,
																		}))
																	}
																	disabled={isActualReadOnly}
																/>
															</Grid2>
															<Grid2 size={{ md: 1.5 }}>
																<PercentField
																	label="Finished Yield"
																	value={secondMonthActuals.ActualFinishedYield}
																	onChange={(v) =>
																		setSecondMonthActuals((p) => ({
																			...p,
																			ActualFinishedYield: v,
																		}))
																	}
																	disabled={isActualReadOnly}
																/>
															</Grid2>
														</Grid2>
													</>
												)}

												<Box mt={2}>
													<Typography
														variant="caption"
														color="text.secondary"
														sx={{ display: "block" }}
													>
														Reported Stacker Finished Tons:{" "}
														{event.StackerFinishedTons
															? round(event.StackerFinishedTons).toLocaleString(
																	"en-US",
																)
															: "-"}
													</Typography>
												</Box>
											</AccordionDetails>
										</Accordion>
									</Grid2>
								)}

								<Grid2 size={{ xs: 12 }}>
									<Card>
										<CardContent>
											<Controller
												name="EventComments"
												control={control}
												render={({ field, fieldState: { error } }) => (
													<CommentsField
														value={field.value ?? ""}
														onChange={field.onChange}
														rows={{ minRows: 2, maxRows: 4 }}
														fieldError={error}
														disabled={disabled}
													/>
												)}
											/>
										</CardContent>
									</Card>
								</Grid2>

								<CampaignDataTable
									selectedEventId={event.EventId}
									setError={setError}
								/>
							</Grid2>
						) : (
							<Grid2
								container
								spacing={1}
								sx={{
									alignItems: "flex-start",
								}}
							>
								<Grid2>
									<Card
										sx={{
											width: "100%",
										}}
									>
										<CardContent>
											<Grid2
												container
												size={12}
												spacing={2}
												style={{ alignItems: "center" }}
											>
												<Grid2
													size={{
														xs: 12,
														md: 3,
													}}
												>
													<Controller
														name="Start"
														control={control}
														rules={{
															required: "Start is required",
															deps: ["End"],
															validate: {
																notAfterEnd: (value) => {
																	if (
																		value &&
																		getValues("End") &&
																		value > getValues("End")!
																	) {
																		return "Start cannot be after End date.";
																	}
																	return true;
																},
															},
														}}
														render={({ field, fieldState: { error } }) => (
															<DateTimePicker
																label="Start"
																timezone={TimeService.getInstance().getServerTimeZone()}
																format="MM/DD/YY HH:mm"
																ampm={false}
																value={field.value ? moment(field.value) : null}
																onChange={field.onChange}
																maxDate={moment(endDate)}
																disabled={disabled}
																slotProps={{
																	textField: {
																		size: "small",
																		fullWidth: true,
																		margin: "none",
																		variant: "outlined",
																		error: !!error,
																		helperText: error?.message,
																	},
																}}
															/>
														)}
													/>
												</Grid2>
												<Grid2
													size={{
														xs: 12,
														md: 3,
													}}
												>
													<Controller
														name="End"
														control={control}
														rules={{
															deps: ["Start"],
															validate: {
																notBeforeStart: (value) => {
																	if (
																		value &&
																		getValues("Start") &&
																		value < getValues("Start")
																	) {
																		return "End cannot be before Start.";
																	}
																	return true;
																},
																notSameAsStart: (value) => {
																	if (
																		value &&
																		startDate &&
																		moment(value).isSame(moment(startDate))
																	) {
																		return "End cannot be the same as Start.";
																	}
																	return true;
																},
															},
														}}
														render={({ field, fieldState: { error } }) => (
															<DateTimePicker
																label="End"
																timezone={TimeService.getInstance().getServerTimeZone()}
																format="MM/DD/YY HH:mm"
																ampm={false}
																value={field.value ? moment(field.value) : null}
																onChange={field.onChange}
																minDate={moment(startDate)}
																disabled={disabled}
																slotProps={{
																	textField: {
																		size: "small",
																		fullWidth: true,
																		variant: "outlined",
																		margin: "none",
																		error: !!error,
																		helperText: error?.message
																			? error.message
																			: "",
																	},
																}}
															/>
														)}
													/>
												</Grid2>
												<Grid2
													size={{
														xs: 12,
														md: 3,
													}}
												>
													<Controller
														name="EventTypeCode"
														control={control}
														render={({ field, fieldState: { error } }) => (
															<EventTypeSelector
																value={field.value}
																onChange={(value: string) =>
																	field.onChange(value)
																}
																fieldError={error}
																disabled
															/>
														)}
													/>
												</Grid2>
												<Grid2 size={{ xs: 12, md: 3 }}>
													<TextField
														label="Duration"
														value={formatDuration(
															getValues("Start"),
															getValues("End"),
														)}
														fullWidth
														size="small"
														variant="outlined"
														disabled
														margin="none"
													/>
												</Grid2>
											</Grid2>
										</CardContent>
									</Card>
								</Grid2>

								<Grid2 size={{ xs: 12 }}>
									<Card>
										<CardContent>
											<Controller
												name="EventComments"
												control={control}
												render={({ field, fieldState: { error } }) => (
													<CommentsField
														value={field.value ?? ""}
														onChange={field.onChange}
														rows={{ minRows: 2, maxRows: 4 }}
														fieldError={error}
														disabled={disabled}
													/>
												)}
											/>
										</CardContent>
									</Card>
								</Grid2>
							</Grid2>
						)}
					</MesfModal.Content>
					<MesfModal.Actions sx={{ px: 4 }}>
						<Grid2
							container
							size={12}
							spacing={1}
							justifyContent="flex-end"
							alignItems="center"
							sx={{ width: "100%" }}
						>
							{!disabled ? (
								<Grid2 size={{ xs: 6 }}>
									<Controller
										name="IsDraft"
										control={control}
										render={({ field }) => (
											<FormControlLabel
												control={
													<Checkbox
														checked={field.value}
														color="primary"
														onChange={field.onChange}
													/>
												}
												label="Is Visible"
											/>
										)}
									/>
								</Grid2>
							) : null}
							<Grid2 size={{ xs: 3 }}>
								<Button
									variant="contained"
									color="inherit"
									onClick={() => onHide(false)}
									fullWidth
								>
									Close
								</Button>
							</Grid2>
							{!disabled ? (
								<Grid2 size={{ xs: 3 }}>
									<ButtonWithLoading
										isLoading={
											upsertEventMutation.isPending ||
											upsertProductionEventMutation.isPending
										}
										variant="contained"
										color="primary"
										type="submit"
										fullWidth
										data-test-id="save-event-button"
									>
										Save
									</ButtonWithLoading>
								</Grid2>
							) : null}
						</Grid2>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
